import { setTitle } from '../main.js'
import { icon, hasIcon } from '../icons.js'
import { fetchImage } from '../images.js'
import { esc } from '../util.js'

function haversineKm(a, b) {
  const R = 6371
  const dLat = (b.lat - a.lat) * Math.PI / 180
  const dLng = (b.lng - a.lng) * Math.PI / 180
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function pickNearby(nodes, coord, indexes, count = 3) {
  const scored = nodes
    .filter(n => n.coord?.lat != null && n.coord?.lng != null)
    .map(n => ({ n, dist: haversineKm(coord, n.coord) }))
    .filter(r => r.dist < 50)
    .sort((a, b) => a.dist - b.dist)

  if (scored.length <= count) return scored

  const cats = new Set()
  const picked = []
  for (const r of scored) {
    if (picked.length >= count) break
    if (cats.has(r.n.category) && picked.length < count - 1) continue
    cats.add(r.n.category)
    picked.push(r)
  }
  while (picked.length < count && scored.length > picked.length) {
    const next = scored.find(r => !picked.includes(r))
    if (next) picked.push(next)
    else break
  }
  return picked
}

async function fetchLlmSuggestion(coord, fresh = false) {
  try {
    const res = await fetch('/api/nearby', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lat: coord.lat, lng: coord.lng, fresh }),
    })
    if (!res.ok) return null
    return res.json()
  } catch { return null }
}

function renderCard(n, dist, ctx, idx) {
  const cat = ctx.indexes.categoryById.get(n.category)
  const catIcon = cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('sparkles')
  const distLabel = dist < 1
    ? `${Math.round(dist * 1000)} m`
    : `${dist.toFixed(1)} km`

  return `
    <a class="nearby-card" href="#/node/${n.id}" data-nearby-idx="${idx}">
      <div class="nearby-card-img" data-nearby-thumb="${esc(n.id)}">${catIcon}</div>
      <div class="nearby-card-body">
        <div class="nearby-card-title">${esc(n.name)}</div>
        ${n.blurb ? `<div class="nearby-card-blurb">${esc(n.blurb.slice(0, 120))}${n.blurb.length > 120 ? '…' : ''}</div>` : ''}
        <div class="nearby-card-meta">
          ${icon('navigation')} ${distLabel}
          ${cat ? ` · ${esc(cat.label)}` : ''}
        </div>
      </div>
    </a>`
}

function renderLlmCard(suggestion) {
  if (!suggestion) return ''
  return `
    <div class="nearby-card llm-card">
      <div class="nearby-card-img llm-gem">${icon('sparkles')}</div>
      <div class="nearby-card-body">
        <div class="nearby-card-title">${esc(suggestion.name)}</div>
        <div class="nearby-card-blurb">${esc(suggestion.description)}</div>
        <div class="nearby-card-meta">
          ${icon('sparkles')} AI-tip
          ${suggestion.distance ? ` · ${esc(suggestion.distance)}` : ''}
        </div>
      </div>
    </div>`
}

let llmTipLoaded = false

function loadLlmTip(coord, root) {
  const llmSlot = root.querySelector('#nearby-llm-slot')
  if (!llmSlot) return
  const fresh = llmTipLoaded
  llmTipLoaded = true
  llmSlot.innerHTML = `
    <div class="nearby-loading small">
      <div class="spinner"></div>
      <p>${icon('sparkles')} AI bedenkt iets extra's…</p>
    </div>`

  fetchLlmSuggestion(coord, fresh).then(result => {
    if (!llmSlot.isConnected) return
    const card = result?.suggestion ? renderLlmCard(result.suggestion) : ''
    llmSlot.innerHTML = `
      ${card}
      <button class="btn secondary nearby-refresh" id="nearby-refresh-btn">
        ${icon('rotate')} Nieuwe AI-tip
      </button>`
    llmSlot.querySelector('#nearby-refresh-btn')?.addEventListener('click', () => {
      loadLlmTip(coord, root)
    })
  })
}

export function renderNearby(root, ctx) {
  setTitle('In de buurt')

  root.innerHTML = `
    <div class="page nearby-page">
      <div class="crumbs"><a href="#/home">‹ Home</a></div>
      <h1 class="title">Wat is er leuk<br>in de buurt?!</h1>
      <p class="subtitle">Krijg 3 suggesties uit je directe omgeving</p>
      <div id="nearby-results" class="nearby-results">
        <div class="nearby-loading">
          <div class="spinner"></div>
          <p>Locatie wordt bepaald…</p>
        </div>
      </div>
    </div>`

  const fallback = ctx.data.meta?.homeBaseCoord
  const resultsEl = root.querySelector('#nearby-results')

  function showResults(coord, isGeo) {
    const nearby = pickNearby(ctx.data.nodes, coord, ctx.indexes, 3)

    if (!nearby.length) {
      resultsEl.innerHTML = `<p class="empty">Geen plekken gevonden in de buurt.</p>`
      return
    }

    const locationNote = isGeo
      ? `${icon('map-pin')} Op basis van je locatie`
      : `${icon('home')} Op basis van thuisbasis (${ctx.data.meta?.homeBaseId || 'chalet'})`

    resultsEl.innerHTML = `
      <div class="nearby-location-note">${locationNote}</div>
      ${nearby.map((r, i) => renderCard(r.n, r.dist, ctx, i)).join('')}
      <div id="nearby-llm-slot" class="nearby-llm-slot">
        <div class="nearby-loading small">
          <div class="spinner"></div>
          <p>${icon('sparkles')} AI bedenkt iets extra's…</p>
        </div>
      </div>
    `

    nearby.forEach(({ n }, i) => {
      fetchImage('node', n, ctx).then(url => {
        if (!url) return
        const slot = root.querySelector(`[data-nearby-thumb="${CSS.escape(n.id)}"]`)
        if (slot) slot.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy"/>`
      })
    })

    loadLlmTip(coord, root)
  }

  if ('geolocation' in navigator) {
    navigator.geolocation.getCurrentPosition(
      pos => showResults({ lat: pos.coords.latitude, lng: pos.coords.longitude }, true),
      () => {
        if (fallback) {
          showResults(fallback, false)
          resultsEl.insertAdjacentHTML('afterbegin', `
            <div class="nearby-geo-denied">
              <p>${icon('crosshair')} Locatietoegang is geblokkeerd. Om dit in te schakelen:</p>
              <ol>
                <li>Tik op het slot-icoon in de adresbalk</li>
                <li>Stel 'Locatie' in op 'Toestaan'</li>
                <li>Herlaad de pagina</li>
              </ol>
            </div>`)
        } else {
          resultsEl.innerHTML = `<p class="empty">Locatie niet beschikbaar en geen thuisbasis ingesteld.</p>`
        }
      },
      { timeout: 8000, maximumAge: 60000 }
    )
  } else if (fallback) {
    showResults(fallback, false)
  } else {
    resultsEl.innerHTML = `<p class="empty">Geolocatie niet ondersteund door je browser.</p>`
  }
}
